/* eslint-disable camelcase */
import React, { Fragment, useContext } from "react";
import PropTypes from "prop-types";
import { Tooltip,Tag } from "../../common/UIComponents";
import { getOrderTypeLabel } from "../../helpers/orders";
import AppConfig from "../../config/AppConfig";
import { isEmpty } from "../../common/Common";
import { OrderConfigContext } from "../../context/OrderConfigContext";
import {getOrderTypeIcon, renderOrderTypeIconWithLabel} from './OrderTypeIcons';
import { renderOrderTypeIconNew } from "./OrderLabelIcon";
import { OrgContext } from "../../context/OrgContext";
import MoveTypeBadge from "../common/MoveTypeBadge";
import { UserContext } from "../../context/UserContext";

const TypeOfOrder = ({
  order,
  orderTypeKey,
  relatedOrderKey,
  showTooltip = true,
  placement = "rightTop",
  showBadge = false,
  triggerNodePosition = true,
  isInAggrid = false,
  isReenderWithIcon = false,
}) => {
  const orderType = getOrderTypeLabel(order, orderTypeKey, relatedOrderKey);
  const { orderConfig = {
    orderTypeLabels: {},
    enhancedOrderTypes: [],
  }, omtByOrderLabel = {} } = useContext(OrderConfigContext);

  const { currentOrg } = useContext(OrgContext);
  const { orgRules } = useContext(UserContext);
  const isCLIEnabled = _.get(orgRules, 'org_billing_rules.cli_order_types', 'false') === 'true';

  // P6c: when the org has a configured OrganizationMoveType for this order's
  // order_label (populated only under service_level_flow_enabled), render the
  // org-configured MoveTypeBadge (short_code + display_color) instead of the
  // hardcoded SVG icon. Absent -> fall through to the legacy icon helpers.
  const configuredOmt = order && order.order_label ? omtByOrderLabel[order.order_label] : null;
  // LEG-level identity survives the DB badge: an R/D leg with a related_order
  // is half of a Move pair (MP / MD), and TR/TD are the transfer legs (SD).
  // order_label can't express which leg a row is, so the badge keeps the org's
  // color/format but carries the legacy leg short form — mirroring
  // getOrderTypeLabel (helpers/orders.js).
  const typeOfOrderVal = order ? order[orderTypeKey] : null;
  const legShortForm =
    ["R", "D"].includes(typeOfOrderVal) && !isEmpty(order?.[relatedOrderKey])
      ? AppConfig.orderTypeShortForms[`M${typeOfOrderVal}`] // MR -> "MP", MD -> "MD"
      : ["TR", "TD"].includes(typeOfOrderVal)
      ? AppConfig.orderTypeShortForms[typeOfOrderVal] // "SD"
      : null;
  // Leg rows often carry no order_label -> no configuredOmt -> they fell to the
  // legacy tiny default <Tag>, mismatching the MoveTypeBadge pills around them.
  // On a DB-badges org (catalog index populated), a leg short form renders the
  // SAME pill geometry, borrowing the legacy color/label maps. Flag-off orgs
  // (empty index) are untouched.
  const dbBadgesActive = Object.keys(omtByOrderLabel).length > 0;
  const configuredBadge = (configuredOmt || (dbBadgesActive && legShortForm)) ? (
    <MoveTypeBadge
      shortCode={legShortForm || configuredOmt.short_code}
      color={configuredOmt?.display_color || AppConfig?.orderTypeColorMapping?.[typeOfOrderVal]}
      title={configuredOmt?.alias_name || AppConfig?.orderTypeLabels?.[typeOfOrderVal]}
    />
  ) : null;

  // Exit early only for paths that actually use orderType
  if (!orderType && !isReenderWithIcon && !showBadge) {
    return null;
  }
  
  // Resolve the correct config entry and display code
  const orderTypeConfigByLabel = _.find(orderConfig.enhancedOrderTypes, { code: order.order_label });
  const orderTypeConfig = (orderTypeConfigByLabel?.extraCode && orderTypeConfigByLabel)
    || (order.extra_code && _.find(orderConfig.enhancedOrderTypes, { extraCode: order.extra_code }));
  const resolvedCode = orderTypeConfig?.extraCode || order.extra_code || order.order_label;

  // Define common properties for Tooltip
  const commonTooltipProps = {
    title: orderConfig.orderTypeLabels[ order[ orderTypeKey ] ],
    placement,
  };

  if (triggerNodePosition && !isInAggrid) {
    commonTooltipProps.getPopupContainer = (triggerNode) => triggerNode.parentElement.parentElement;
  }

  // Define the content to be shown, with or without Tooltip
  const content = showTooltip && !isEmpty(order[ orderTypeKey ]) ? (
    <Tooltip { ...commonTooltipProps } overlayStyle={ { minWidth: 70 } } overlayClassName="tooltip_point">
      { orderType }
    </Tooltip>
  ) : (
    orderType
  );

  // DB-driven cell with the move type NAME beside the badge — parity with the
  // legacy icon+label rendering ("[D] Delivery", not just "[D]"). Leg pills
  // without an OMT label with the legacy type name ("Pickup"/"Delivery").
  const configuredBadgeWithName = configuredBadge ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {configuredBadge}
      <span>{configuredOmt?.alias_name || AppConfig?.orderTypeLabels?.[typeOfOrderVal] || ""}</span>
    </span>
  ) : null;

  // Define the Tag if showBadge is true
  const getLabel =  () =>
  (<Fragment>
    {
      configuredBadgeWithName
        || renderOrderTypeIconWithLabel(orderConfig.enhancedOrderTypes, resolvedCode, { width: '20px', height: '20px' }, isCLIEnabled)
    }
  </Fragment>)
  const tag = showBadge ? (
    <div >
      {configuredBadge
        || renderOrderTypeIconNew(order, resolvedCode, orderTypeKey, isInAggrid, currentOrg?.code, isCLIEnabled)}
    </div>
  ) : null;
  


  return <>{ isReenderWithIcon ? getLabel() : showBadge ? tag : content }</>;
};

export default TypeOfOrder;

TypeOfOrder.propTypes = {
  order: PropTypes.shape().isRequired,
  orderTypeKey: PropTypes.string.isRequired,
  relatedOrderKey: PropTypes.string.isRequired,
};
